import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { OpenOrderInvoice } from 'smg/customer-api/v2/orders';
import * as models from 'smg/customer-api/models';
import { GetOrder } from 'smg/customer-api/orders';
import { ShowError } from 'smg/errors';
import * as session from 'smg/customer-api/session';
import * as html from 'smg/html';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../account-session';
import * as carts from '../carts';
import * as lineitems from '../lineitems';
import * as payments from '../payments';
import { DrawPaymentOption } from '../payments';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const sess = await session.Session().catch(() => undefined);

    const spinner = document.querySelector('#spinner');

    function showNotFound() {
        spinner?.classList.add('d-none');
        const notFound = document.querySelector('#not-found');

        notFound?.classList.remove('d-none');
    }

    const tableContainer = document.querySelector('#order-table') as HTMLElement;
    const invoiceNumberEl = document.querySelector('#invoice-number') as HTMLElement;
    const thumbContainer = document.querySelector('#thumb-container') as HTMLElement;
    const orderCreated = document.querySelector('#order-placed') as HTMLElement;
    const orderTotal = document.querySelector('#order-total') as HTMLElement;
    const orderItems = document.querySelector('#order-items') as HTMLElement;
    const shippingContainer = document.querySelector('#shipping-options-container') as HTMLElement;
    const cartPaymentContainer = document.querySelector('#cart-payment-container') as HTMLElement;
    const poCartPaymentContainer = document.querySelector('#po-cart-payment-container') as HTMLElement;
    const nonPaymentWarning = document.querySelector('#non-payment-warning') as HTMLElement;
    const poNonPaymentWarning = document.querySelector('#po-non-payment-warning') as HTMLElement;
    const cartPaymentWarnIcon = document.querySelector('#warning-icon') as HTMLElement;
    const poCartPaymentWarnIcon = document.querySelector('#po-warning-icon') as HTMLElement;
    const totalNum = document.querySelector('#balance-total') as HTMLElement;
    const poTotalNum = document.querySelector('#po-balance-total') as HTMLElement;
    const paidNum = document.querySelector('#balance-payment') as HTMLElement;
    const poPaidNum = document.querySelector('#po-balance-payment') as HTMLElement;
    const balanceNum = document.querySelector('#balance-remaining') as HTMLElement;
    const poBalanceNum = document.querySelector('#po-balance-remaining') as HTMLElement;
    const makePaymentBtn = document.querySelector('.make-payment-btn') as HTMLElement;
    const cardContainer = document.querySelector<HTMLElement>('#payment-methods-container');

    const key = QueryString().get('id');

    if (!key) {
        showNotFound();
        return;
    }

    GetOrder(key, {
        data: 'id,key,invoiceNumber,created,total,paid,balance,orderLineItems,orderShippingOptions,orderProofs,orderPayments',
    }).then(async resp => {
        const order = resp;
        if (!order) {
            showNotFound();
            return;
        }

        const itemQuantity = (arr: models.OrderLineItem[]) => {
            let totalQuantity = 0;

            arr.forEach(lineItem => {
                if (lineItem.lineItemModel?.product || !lineItem.lineItemModel) {
                    totalQuantity += lineItem.quantity;
                }
            });

            return totalQuantity;
        };

        tableContainer.append(await lineitems.Table(order.orderLineItems));
        invoiceNumberEl.innerText = String(order.invoiceNumber);
        orderTotal.innerText = DollarFormat(order.total);
        orderItems.innerText = String(itemQuantity(order.orderLineItems));
        orderCreated.innerText = order.created.isValid ? order.created.toLocaleString(DateTime.DATETIME_SHORT) : '';

        if (order.orderShippingOptions.length) {
            for (const shippingAddress of order.orderShippingOptions) {
                const el = template.Clone('#shipping-template');
                const addressEl = el.querySelector('address');
                (addressEl as HTMLElement).classList.add('border');

                const shippingOption = shippingAddress.shippingOption;
                if (shippingOption) {
                    (addressEl as HTMLElement).innerHTML = html.Nl2br(html.Escape(shippingOption.address?.toStringWithoutContact() ?? ''));
                }
                (addressEl as HTMLElement).classList.add('fs-5');

                shippingContainer.append(addressEl as HTMLElement);
                shippingAddress.orderShippingOptionShipments.forEach(orderShippingOptionShipment => {
                    const el = template.Clone('.shipment-template');
                    const shipmentWrapper = el.querySelector('.shipment-wrapper') as HTMLDivElement;
                    const carrier = el.querySelector('.carrier');
                    const shipped = el.querySelector('.shipped');
                    const delivered = el.querySelector('.delivered');
                    const trackingNum = el.querySelector('a.tracking-number') as HTMLAnchorElement;
                    const trackingNumSpan = el.querySelector('span.tracking-number');
                    const shipment = orderShippingOptionShipment.shipment;
                    shipmentWrapper.classList.add('border');
                    (carrier as HTMLElement).textContent = shipment?.carrier?.name || '';
                    if (shipment?.shippedAt.isValid) {
                        (shipped as HTMLElement).textContent = shipment.shippedAt.toLocaleString(DateTime.DATETIME_SHORT);
                    } else if (shipped?.parentElement) {
                        shipped.parentElement.classList.add('d-none');
                    }
                    if (shipment?.deliveredAt.isValid) {
                        (delivered as HTMLElement).textContent = shipment.deliveredAt.toLocaleString(DateTime.DATETIME_SHORT);
                        shipmentWrapper.classList.add('bg-primary-30');
                    } else if (delivered?.parentElement) {
                        delivered.parentElement.classList.add('d-none');
                        shipmentWrapper.classList.add('bg-secondary-30');
                    }
                    trackingNum.textContent = shipment?.trackingNumber || '';
                    if (shipment?.carrier?.link) {
                        trackingNum.href = shipment.carrier.trackingURL(shipment.trackingNumber);
                    } else if (trackingNumSpan) {
                        trackingNum.classList.add('d-none');
                        trackingNumSpan.classList.remove('d-none');
                        trackingNumSpan.textContent = shipment?.trackingNumber || '';
                    }

                    (addressEl as HTMLElement).append(shipmentWrapper);

                    shipmentWrapper.style.fontSize = '1rem';
                });
            }
        }

        const drawPayment = async (paymentOption: models.PaymentOption, orderPayment?: models.OrderPayment) => {
            const paymentEl = (await DrawPaymentOption(models.PaymentOption.fromJSON(paymentOption), models.OrderPayment.fromJSON(orderPayment))).querySelector<HTMLElement>('div');
            const cardEl = template.Clone('#card-template').firstElementChild as HTMLElement;
            const paymentContainer = cardEl.querySelector('.payment-container') as HTMLElement;
            paymentContainer?.append(paymentEl as HTMLElement);
            cardContainer?.insertBefore(cardEl, cardContainer.firstChild);
        };

        for (const paymentMethod of order.orderPayments) {
            if (paymentMethod.paymentOption) {
                await drawPayment(paymentMethod.paymentOption, paymentMethod);
            }
            const paymentAmount = document.querySelector('#payment-amount') as HTMLElement;
            paymentAmount.innerText = DollarFormat(paymentMethod.total);
        }

        for (const orderProof of order.orderProofs) {
            const proofEl = template.Clone('#proof-template');
            const imgEl = proofEl.querySelector('img') as HTMLImageElement;
            const anchorEl = proofEl.querySelector('a') as HTMLAnchorElement;

            anchorEl.href = orderProof.proof?.main || '';
            anchorEl.dataset.caption = orderProof.proof?.name || '';

            imgEl.src = orderProof.proof?.thumbnail || '';

            thumbContainer?.append(proofEl);
        }

        const balance = order.balance;
        if (balance.gt('0.05')) {
            makePaymentBtn.classList.remove('d-none');
            makePaymentBtn.addEventListener('click', () =>
                payments.PromptPayment(sess?.user, [order.id], balance).
                    then(() => window.location.reload()),
            );
        }

        const reorder = document.querySelector('#order-reorder') as HTMLButtonElement;
        // $0 reorders are rejected server-side (#7049), so hide the button instead of wiring a dead click handler
        if (order.total.eq(0)) {
            reorder.classList.add('d-none');
        } else {
            reorder.addEventListener('click', async () => {
                buttons.Start(reorder);
                await carts.AddCartItem({ orderID: order.key }).then(() => {
                    reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                    reorder.classList.add('btn-outline-success');

                    reorder.querySelector('i')?.classList.remove('fa-shopping-cart');
                    reorder.querySelector('i')?.classList.add('fa-check');
                }).finally(() => buttons.Stop(reorder));
            });
        }

        const invoice = document.querySelector('#order-invoice') as HTMLButtonElement;
        invoice.onclick = () => {
            buttons.Start(invoice);
            void OpenOrderInvoice(`id:${order.id}`).catch(ShowError).finally(() => buttons.Stop(invoice));
        };

        function hasPo(order: models.Order) {
            for (const orderPayment of order.orderPayments) {
                if (orderPayment.paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                    return true;
                }
            }
            return false;
        }

        if (!hasPo(order) && balance.gt('0.05')) {
            cartPaymentContainer.classList.remove('d-none');
            totalNum.innerText = `$${order.total.toFixed(2)}`;
            paidNum.innerText = `$${order.paid.toFixed(2)}`;
            balanceNum.innerText = `$${order.balance.toFixed(2)}`;
            cartPaymentContainer.classList.add('alert', 'alert-warning', 'w-75');
            cartPaymentWarnIcon.classList.remove('d-none');
            nonPaymentWarning.classList.remove('d-none');
            poCartPaymentContainer.classList.add('d-none');
        } else if (hasPo(order) && balance.gt('0.05')) {
            poCartPaymentContainer.classList.remove('d-none');
            poCartPaymentContainer.classList.add('d-flex');
            poTotalNum.innerText = `$${order.total.toFixed(2)}`;
            poPaidNum.innerText = `$${order.paid.toFixed(2)}`;
            poBalanceNum.innerText = `$${order.balance.toFixed(2)}`;
            poCartPaymentContainer.classList.add('alert', 'alert-success', 'w-75');
            poCartPaymentWarnIcon.classList.remove('d-none');
            poNonPaymentWarning.classList.remove('d-none');
            cartPaymentContainer.classList.remove('alert', 'alert-warning');
            cartPaymentWarnIcon.classList.add('d-none');
            nonPaymentWarning.classList.add('d-none');
            cartPaymentContainer.classList.add('d-none');
        } else {
            poCartPaymentContainer.classList.remove('alert', 'alert-warning');
            poCartPaymentWarnIcon.classList.add('d-none');
            poNonPaymentWarning.classList.add('d-none');
            poCartPaymentContainer.classList.add('d-none');
            cartPaymentContainer.classList.remove('alert', 'alert-warning');
            cartPaymentWarnIcon.classList.add('d-none');
            nonPaymentWarning.classList.add('d-none');
            cartPaymentContainer.classList.add('d-none');
        }

        const spinner = document.querySelector('#spinner');
        const orderContainer = document.querySelector('#order-container');
        spinner?.classList.add('d-none');
        orderContainer?.classList.remove('d-none');
    }).catch(err => {
        showNotFound();
        throw err;
    });
}
